import React, { useEffect, useMemo, useState } from 'react';
import { getRoles, getAllPermissions, createRole, updateRole, deleteRole } from '../api';
import './RoleManager.css';

const emptyForm = { name: '', description: '', permissionIds: [] };

const groupPermissions = (permissions) => {
  const groups = {};
  permissions.forEach((permission) => {
    const [prefix] = permission.name.includes('.') ? permission.name.split('.') : ['General'];
    const groupName = permission.name.includes('.') ? prefix : 'General';
    if (!groups[groupName]) groups[groupName] = [];
    groups[groupName].push(permission);
  });
  return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0]));
};

const RoleManager = () => {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null); // null = create mode
  const [formData, setFormData] = useState(emptyForm);
  const [permissionSearch, setPermissionSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const fetchAll = async () => {
    setLoading(true);
    setError('');
    try {
      const [rolesData, permissionsData] = await Promise.all([getRoles(), getAllPermissions()]);
      setRoles(Array.isArray(rolesData) ? rolesData : []);
      setPermissions(Array.isArray(permissionsData) ? permissionsData : []);
    } catch (err) {
      setError(err?.message || 'Failed to load roles and permissions.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const groupedPermissions = useMemo(() => groupPermissions(permissions), [permissions]);

  const filteredGroupedPermissions = useMemo(() => {
    const term = permissionSearch.trim().toLowerCase();
    if (!term) return groupedPermissions;
    return groupedPermissions
      .map(([group, perms]) => [group, perms.filter((p) => p.name.toLowerCase().includes(term))])
      .filter(([, perms]) => perms.length > 0);
  }, [groupedPermissions, permissionSearch]);

  const isProtectedRole = (role) => String(role.name || '').toLowerCase().includes('super');

  const openCreateModal = () => {
    setEditingRole(null);
    setFormData(emptyForm);
    setPermissionSearch('');
    setFormError('');
    setShowModal(true);
  };

  const openEditModal = (role) => {
    setEditingRole(role);
    setFormData({
      name: role.name || '',
      description: role.description || '',
      permissionIds: (role.permissions || []).map((p) => p.id),
    });
    setPermissionSearch('');
    setFormError('');
    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
  };

  const togglePermission = (permissionId) => {
    setFormData((prev) => ({
      ...prev,
      permissionIds: prev.permissionIds.includes(permissionId)
        ? prev.permissionIds.filter((id) => id !== permissionId)
        : [...prev.permissionIds, permissionId],
    }));
  };

  const toggleGroup = (groupPerms, selectAll) => {
    const groupIds = groupPerms.map((p) => p.id);
    setFormData((prev) => ({
      ...prev,
      permissionIds: selectAll
        ? Array.from(new Set([...prev.permissionIds, ...groupIds]))
        : prev.permissionIds.filter((id) => !groupIds.includes(id)),
    }));
  };

  const handleSave = async () => {
    setFormError('');
    if (!formData.name.trim()) {
      setFormError('Role name is required.');
      return;
    }

    setSaving(true);
    try {
      if (editingRole) {
        await updateRole(editingRole.id, {
          name: formData.name.trim(),
          description: formData.description,
          permissionIds: formData.permissionIds,
        });
        setSuccessMessage(`"${formData.name.trim()}" updated successfully.`);
      } else {
        await createRole({
          name: formData.name.trim(),
          description: formData.description,
          permissionIds: formData.permissionIds,
        });
        setSuccessMessage(`"${formData.name.trim()}" created successfully.`);
      }
      setShowModal(false);
      await fetchAll();
      setTimeout(() => setSuccessMessage(''), 4000);
    } catch (err) {
      setFormError(err?.message || err?.errors?.name?.[0] || 'Failed to save role.');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (role) => {
    setDeleteTarget(role);
    setDeleteError('');
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError('');
    try {
      await deleteRole(deleteTarget.id);
      setSuccessMessage(`"${deleteTarget.name}" deleted successfully.`);
      setDeleteTarget(null);
      await fetchAll();
      setTimeout(() => setSuccessMessage(''), 4000);
    } catch (err) {
      setDeleteError(err?.message || 'Failed to delete role.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="role-manager-panel">
      <div className="role-manager-header">
        <div>
          <h3>Roles &amp; Permissions</h3>
          <p className="role-manager-description">
            Create roles and control which permissions each one grants.
          </p>
        </div>
        <button type="button" className="role-btn-primary" onClick={openCreateModal}>
          + Add Role
        </button>
      </div>

      {successMessage && <div className="role-manager-success">{successMessage}</div>}

      {loading ? (
        <p className="role-manager-empty">Loading roles...</p>
      ) : error ? (
        <div className="role-manager-error">{error}</div>
      ) : roles.length === 0 ? (
        <p className="role-manager-empty">No roles found.</p>
      ) : (
        <div className="role-card-grid">
          {roles.map((role) => (
            <div key={role.id} className="role-card">
              <div className="role-card-top">
                <span className="role-card-name">{role.name}</span>
                <span className="role-card-count">{(role.permissions || []).length} permissions</span>
              </div>
              {role.description && <p className="role-card-description">{role.description}</p>}
              <div className="role-card-actions">
                <button type="button" className="role-card-btn edit" onClick={() => openEditModal(role)}>
                  Edit
                </button>
                <button
                  type="button"
                  className="role-card-btn delete"
                  onClick={() => confirmDelete(role)}
                  disabled={isProtectedRole(role)}
                  title={isProtectedRole(role) ? 'The Superadmin role cannot be deleted' : ''}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="role-modal-overlay" onClick={closeModal}>
          <div className="role-modal" onClick={(e) => e.stopPropagation()}>
            <div className="role-modal-header">
              <h3>{editingRole ? `Edit "${editingRole.name}"` : 'Add Role'}</h3>
              <button type="button" className="role-modal-close" onClick={closeModal} disabled={saving}>
                ✕
              </button>
            </div>

            <div className="role-modal-body">
              <div className="role-form-row">
                <label htmlFor="role-name">Role Name</label>
                <input
                  id="role-name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                  disabled={saving}
                />
              </div>
              <div className="role-form-row">
                <label htmlFor="role-description">Description</label>
                <textarea
                  id="role-description"
                  value={formData.description}
                  onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                  disabled={saving}
                  rows={2}
                />
              </div>

              <div className="role-form-row">
                <label htmlFor="permission-search">
                  Permissions ({formData.permissionIds.length} selected)
                </label>
                <input
                  id="permission-search"
                  type="text"
                  placeholder="Search permissions..."
                  value={permissionSearch}
                  onChange={(e) => setPermissionSearch(e.target.value)}
                  disabled={saving}
                />
              </div>

              <div className="permission-groups">
                {filteredGroupedPermissions.map(([group, perms]) => {
                  const allSelected = perms.every((p) => formData.permissionIds.includes(p.id));
                  return (
                    <div key={group} className="permission-group">
                      <div className="permission-group-header">
                        <span className="permission-group-name">{group}</span>
                        <button
                          type="button"
                          className="permission-group-toggle-all"
                          onClick={() => toggleGroup(perms, !allSelected)}
                          disabled={saving}
                        >
                          {allSelected ? 'Deselect all' : 'Select all'}
                        </button>
                      </div>
                      <div className="permission-chip-list">
                        {perms.map((permission) => {
                          const active = formData.permissionIds.includes(permission.id);
                          return (
                            <button
                              key={permission.id}
                              type="button"
                              className={`permission-chip ${active ? 'active' : ''}`}
                              onClick={() => togglePermission(permission.id)}
                              disabled={saving}
                            >
                              {active && <span className="permission-chip-check">✓</span>}
                              {permission.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
                {filteredGroupedPermissions.length === 0 && (
                  <p className="role-manager-empty">No permissions match "{permissionSearch}".</p>
                )}
              </div>

              {formError && <div className="role-manager-error">{formError}</div>}
            </div>

            <div className="role-modal-footer">
              <button type="button" className="role-btn-cancel" onClick={closeModal} disabled={saving}>
                Cancel
              </button>
              <button type="button" className="role-btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save Role'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="role-modal-overlay" onClick={() => !deleting && setDeleteTarget(null)}>
          <div className="role-modal role-delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="role-modal-header">
              <h3>Delete "{deleteTarget.name}"?</h3>
            </div>
            <div className="role-modal-body">
              <p>This cannot be undone. Any admin currently assigned this role must be moved to a different role first.</p>
              {deleteError && <div className="role-manager-error">{deleteError}</div>}
            </div>
            <div className="role-modal-footer">
              <button type="button" className="role-btn-cancel" onClick={() => setDeleteTarget(null)} disabled={deleting}>
                Cancel
              </button>
              <button type="button" className="role-card-btn delete role-delete-confirm-btn" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete Role'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleManager;
